import type { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';
import { BORDER_WIDTH_CSS_PROPERTY_TYPES } from '../constants';
import { decompose } from './decomposer';

/**
 * Result of interpreting a CSS value.
 * This is used by Meaningfulness rules to decide "meaningless vs meaningful".
 */
export interface ValueInterpretation {
  isEmpty: boolean;
  isZero: boolean;
  hasValue: boolean;
  rawValue: string | undefined;
}

export type MeaningCheckFn = (
  value: string | undefined,
  propertyType: string,
) => boolean;

/**
 * Single source of truth for:
 * - interpreting raw CSS values
 * - determining "meaningless vs meaningful" per rule
 */
export class Meaningfulness {
  static interpret(
    value: string | undefined,
    propertyType: string,
  ): ValueInterpretation {
    // Handle empty/undefined values
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      value.trim() === ''
    ) {
      return {
        isEmpty: true,
        isZero: false,
        hasValue: false,
        rawValue: value,
      };
    }

    const trimmedValue = value.trim();

    // CSS variables are always considered to have value (user requirement)
    if (trimmedValue.startsWith('var(')) {
      return {
        isEmpty: false,
        isZero: false,
        hasValue: true,
        rawValue: value,
      };
    }

    // Keywords are treated as no value (inherit, initial, etc.)
    if (
      ['inherit', 'initial', 'unset', 'revert', 'none'].includes(
        trimmedValue.toLowerCase(),
      )
    ) {
      return {
        isEmpty: false,
        isZero: false,
        hasValue: false,
        rawValue: value,
      };
    }

    // Check if value is zero using regex (covers: 0, 0px, 0em, 0spx, 0%, etc.)
    const isZeroValue =
      /^0(px|spx|em|rem|%|vh|vw|vmin|vmax|ch|ex|cm|mm|in|pt|pc)?$/i.test(
        trimmedValue,
      );

    // Try to decompose if it's a shorthand property
    const decomposed = decompose(propertyType, trimmedValue);

    if (decomposed) {
      // Check if all decomposed values are zero or null
      const allZeroOrNull = Object.values(decomposed).every((val) => {
        if (val === null || val === undefined || val === '') {
          return true;
        }
        return /^0(px|spx|em|rem|%|vh|vw|vmin|vmax|ch|ex|cm|mm|in|pt|pc)?$/i.test(
          val.trim(),
        );
      });

      return {
        isEmpty: false,
        isZero: allZeroOrNull,
        hasValue: !allZeroOrNull,
        rawValue: value,
      };
    }

    // For atomic properties
    return {
      isEmpty: false,
      isZero: isZeroValue,
      hasValue: !isZeroValue,
      rawValue: value,
    };
  }

  static isMeaningfulByAction(
    actionName: StylesDerivedNativeActionNames,
    interpretation: ValueInterpretation,
  ): boolean {
    // Empty values are never meaningful
    if (interpretation.isEmpty) {
      return false;
    }

    switch (actionName) {
      case StylesDerivedNativeActions.borderStyles:
      case StylesDerivedNativeActions.strokeStyles:
        // Meaningful: non-empty AND has actual value (not zero, not keyword-only)
        return interpretation.hasValue;

      case StylesDerivedNativeActions.cornerStyles:
      case StylesDerivedNativeActions.spacingStyles:
        // Meaningful: non-empty AND (has value OR is zero)
        // Zero values are meaningful for corners/spacing
        return interpretation.hasValue || interpretation.isZero;

      case StylesDerivedNativeActions.shadowStyles:
        // Meaningful: non-empty AND has value
        // `none` is keyword-only => hasValue=false => not meaningful
        return interpretation.hasValue;

      case StylesDerivedNativeActions.textStyles:
        // Meaningful: non-empty AND (has value OR is zero)
        return interpretation.hasValue || interpretation.isZero;

      default:
        // Default: non-empty AND (has value OR is zero)
        return interpretation.hasValue || interpretation.isZero;
    }
  }

  static isMeaningfulValue(
    actionName: StylesDerivedNativeActionNames,
    value: string | undefined,
    propertyType: string,
  ): boolean {
    return Meaningfulness.isMeaningfulByAction(
      actionName,
      Meaningfulness.interpret(value, propertyType),
    );
  }

  static meaningCheck(
    actionName: StylesDerivedNativeActionNames,
  ): MeaningCheckFn {
    return (value, propertyType) =>
      Meaningfulness.isMeaningfulValue(actionName, value, propertyType);
  }
}
